import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { api, jsonBody } from "../api";
import { PageHeader } from "../components/PageHeader";

export function SettingsPage() {
  const [settings, setSettings] = useState<any>(null);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const load = () =>
    api<{ settings: any }>("/admin/settings")
      .then((result) => setSettings(result.settings))
      .catch((error) => setMessage({ type: "error", text: error.message }));
  useEffect(() => {
    void load();
  }, []);
  if (!settings)
    return message ? (
      <Alert severity="error">{message.text}</Alert>
    ) : (
      <CircularProgress />
    );

  const set = (key: string) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setSettings({
      ...settings,
      [key]:
        event.target.type === "number"
          ? Number(event.target.value)
          : event.target.value,
    });
  const toggle =
    (key: string) => (event: React.ChangeEvent<HTMLInputElement>) =>
      setSettings({ ...settings, [key]: event.target.checked });

  return (
    <>
      <PageHeader
        title="Runtime settings"
        subtitle="Changes apply without redeploying. Azure credentials are managed only on the Azure pool page."
      />
      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}
      <Card>
        <CardContent>
          <Stack gap={3} maxWidth={850}>
            <Typography variant="h6">Processing controls</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.enabled}
                  onChange={toggle("enabled")}
                />
              }
              label="OCR processing enabled"
            />

            <Typography variant="h6" mt={1}>
              Azure pool routing
            </Typography>
            <Stack direction={{ xs: "column", md: "row" }} gap={2}>
              <TextField
                type="number"
                label="Free recovery threshold %"
                value={settings.azureFreeRecoveryThreshold}
                onChange={set("azureFreeRecoveryThreshold")}
                helperText="While using Paid, switch back only when Free has at least this capacity"
                fullWidth
              />
              <TextField
                type="number"
                label="Provider cooldown seconds"
                value={settings.azureFailureCooldownSeconds}
                onChange={set("azureFailureCooldownSeconds")}
                fullWidth
              />
              <TextField
                type="number"
                label="Failure threshold"
                value={settings.azureFailureThreshold}
                onChange={set("azureFailureThreshold")}
                fullWidth
              />
            </Stack>

            <Typography variant="h6" mt={1}>
              Processing limits
            </Typography>
            <Stack direction={{ xs: "column", md: "row" }} gap={2}>
              <TextField
                type="number"
                label="Max files / scan"
                value={settings.maxFilesPerScan}
                onChange={set("maxFilesPerScan")}
                fullWidth
              />
              <TextField
                type="number"
                label="Max file bytes"
                value={settings.maxFileSizeBytes}
                onChange={set("maxFileSizeBytes")}
                fullWidth
              />
              <TextField
                type="number"
                label="Max OCR attempts"
                value={settings.maxOcrAttempts}
                onChange={set("maxOcrAttempts")}
                fullWidth
              />
              <TextField
                type="number"
                label="Azure interval ms"
                value={settings.azureMinIntervalMs}
                onChange={set("azureMinIntervalMs")}
                fullWidth
              />
            </Stack>

            <Typography variant="h6" mt={1}>
              Retention and alerts
            </Typography>
            <Stack direction={{ xs: "column", md: "row" }} gap={2}>
              <TextField
                type="number"
                label="Default retention days"
                value={settings.retentionDays}
                onChange={set("retentionDays")}
                fullWidth
              />
              <TextField
                type="number"
                label="Quota warning %"
                value={settings.quotaWarningPercentage}
                onChange={set("quotaWarningPercentage")}
                fullWidth
              />
            </Stack>
            <Stack direction="row" gap={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.retentionEnabled}
                    onChange={toggle("retentionEnabled")}
                  />
                }
                label="Scheduled retention enabled"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.webhookEnabled}
                    onChange={toggle("webhookEnabled")}
                  />
                }
                label="Webhooks enabled"
              />
            </Stack>
            <div>
              <Button
                variant="contained"
                onClick={async () => {
                  try {
                    await api("/admin/settings", {
                      method: "PATCH",
                      body: jsonBody(settings),
                    });
                    setMessage({ type: "success", text: "Settings saved" });
                    void load();
                  } catch (error) {
                    setMessage({
                      type: "error",
                      text:
                        error instanceof Error ? error.message : "Save failed",
                    });
                  }
                }}
              >
                Save settings
              </Button>
            </div>
          </Stack>
        </CardContent>
      </Card>
    </>
  );
}
